import { Reducer } from "redux";
import { cloneDeep } from "lodash";
import { Models, Types, Components, Interfaces, Constants, Logger, Utils } from "@sendum/core";

import { DispatchAction } from "./VisibilityStore";
import { TAction, VisibilityActionTypes } from "./VisibilityActions";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("VisibilityReducer");

export interface IVisibilityState {
	shipment?: Models.Shipment;
	shipments: Models.Shipment[];
	devices: Models.Device[];
	device?: Models.Device;
	present?: Models.State;
	history: Map<string, Types.TProperty[]>;
	rules?: Models.Rules;
	points: Types.TPin[];
	currentDeviceId?: string;
	sensors: string[];
	selectedSensor?: string;
	multiplePlaceComponent: "graph" | "reports" | "rules" | "alarms" | "map";
	error?: Error;
	isLoadingShipment?: boolean;
	isLoadingShipments?: boolean;
	historyLoading: Map<string, boolean>;
	rulesLoading: Map<string, boolean>;
	isClearAllAlarms?: boolean;
	clearAllAlarmsReason?: string;
	isVirtualShipment?: boolean;
	orderId?: string;
	trackingStartTime?: number;
	/** trackingEndTime is used for realtime message processing.
	 * values: undefined | 0 | 1626423294000(epochTime)
	 * undefined - Mqtt messages will proceed.
	 * 0 - shipment or Leg not started yet. Mqtt will be rejected.
	 * 1626423294000(epochTime) - shipment or Leg is completed. Mqtt will be rejected.
	 */
	trackingEndTime?: number;
	isUpdateDevicePresent?: boolean;
	view?: string;
	legs: string[];
	isLiveLegExtended?: boolean;
}

const initialState: IVisibilityState = {
	shipments: [],
	devices: [],
	isLoadingShipment: true,
	isLoadingShipments: true,
	historyLoading: new Map<string, boolean>(),
	history: new Map<string, Types.TProperty[]>(),
	points: new Array<Types.TPin>(),
	sensors: [],
	multiplePlaceComponent: "graph",
	rulesLoading: new Map<string, boolean>(),
	isClearAllAlarms: false,
	isUpdateDevicePresent: true,
	legs: [],
};

const setLoadingHistory = (state: IVisibilityState, payload: TAction): IVisibilityState => {
	const { deviceId, shipmentId, sensor, isLoadingHistory } = payload;
	if (!deviceId || !shipmentId || !sensor || isLoadingHistory === undefined) {
		return state;
	}
	const historyLoading: Map<string, boolean> = new Map(state.historyLoading);
	historyLoading.set(`${deviceId}#${shipmentId}#${sensor}`, isLoadingHistory);

	return {
		...state,
		historyLoading,
	};
};

const setShipment = (state: IVisibilityState, payload: TAction): IVisibilityState => {
	const { shipment, deviceId } = payload;
	let { error, device } = state;
	if (shipment?.devices?.length) {
		const deviceIndex = shipment.devices.findIndex((d: Models.Device) => deviceId && d?.id === deviceId);
		if (deviceIndex === Constants.BAD_INDEX && deviceId) {
			error = new Error(`Order ${shipment.masterTrackingNo} does not have container ${shipment.formatErrorDeviceName(deviceId)} `);
		}
		device = deviceIndex !== Constants.BAD_INDEX ? shipment.devices[deviceIndex] : shipment.devices[0];
		if (device) {
			device.shipment = shipment;
		}
	}

	return {
		...state,
		shipment,
		error,
		device,
		devices: shipment?.devices ?? [],
		currentDeviceId: device?.id,
		isLoadingShipment: false,
		history: new Map<string, Types.TProperty[]>(),
		points: device ? Components.MapUtils.buildPoints(new Array<Types.TPin>(), state.history.get(`${device.id}#${device.shipment.id}#location`) as Types.TLocationProperty[]) : [],
		sensors: device ? device.getSelectedSensorsConfig() : [],
		present: device?.present,
	};
};

const addHistory = (state: IVisibilityState, payload: TAction): IVisibilityState => {
	const { deviceId, shipmentId, sensorHistory, sensor } = payload;
	const key = `${deviceId ?? ""}#${shipmentId ?? ""}#${sensor ?? ""}`;
	const currentDeviceHistory: Types.TProperty[] = state.history.get(key) || new Array<Types.TProperty>();
	const newDeviceHistory: Types.TProperty[] = currentDeviceHistory.concat(sensorHistory ?? []);

	const currentHistory: Map<string, Types.TProperty[]> = state.history;

	currentHistory.set(key, newDeviceHistory);

	if (sensor === "location") {
		return {
			...state,
			points: (Components.MapUtils.buildPoints(state.points, currentHistory.get(key) as Types.TLocationProperty[])),
			history: new Map(currentHistory),
		};
	}

	return {
		...state,
		history: new Map(currentHistory),
	};
};

const updateAlarm = (state: IVisibilityState, payload: TAction): IVisibilityState => {
	const { deviceId, alarm } = payload;
	const newState = {
		...state,
	};
	if (state.shipment) {
		const { shipment } = state;
		const device = shipment.devices.find(d => d.id === deviceId);
		const deviceIndex = shipment.devices.findIndex(d => d.id === deviceId);
		if (device) {
			const currentAlarms = [...device.alarms];
			const alarmIndex = currentAlarms.findIndex(a => a.timestamp === alarm?.timestamp && a.sensor.replace("SHIPMENT:alarms:", "").replace("REPORT:alarms:", "") === alarm.sensor);
			if (alarmIndex !== -1 && alarm) {
				currentAlarms[alarmIndex].clearedBy = currentAlarms[alarmIndex].clearedBy || alarm.clearedBy;
				currentAlarms[alarmIndex].clearedTimestamp = currentAlarms[alarmIndex].clearedTimestamp || alarm.clearedTimestamp;
				currentAlarms[alarmIndex].reason = alarm.reason;
			}
			device.alarms = currentAlarms;
			shipment.devices[deviceIndex] = device;
			newState.shipment = shipment;
			if (state.device && state.device.id === deviceId) {
				newState.device = device;
			}
		}
	}

	return newState;
};

const addStateToHistory = (state: IVisibilityState, deviceId: string, shipmentId: string, message: Models.State): IVisibilityState => {
	const currentHistory: Map<string, Types.TProperty[]> = state.history;
	if (state.device) {
		["location"].concat(Array.from(state.device?.getAttributesConfig().keys())).forEach(sensor => {
			if (message[sensor]) {
				const currentDeviceHistory: Types.TProperty[] | undefined = state.history.get(`${deviceId}#${shipmentId}#${sensor}`);
				if (currentDeviceHistory) {
					const newDeviceHistory: Types.TProperty[] = currentDeviceHistory.concat([message[sensor]]);
					currentHistory.set(`${deviceId}#${shipmentId}#${sensor}`, newDeviceHistory);
				}
			}
		});

		return {
			...state,
			history: new Map(currentHistory),
			points: message["location"] ? Components.MapUtils.buildPoints(state.points, currentHistory.get(`${deviceId}#${shipmentId}#location`) as Types.TLocationProperty[]) : state.points,
		};
	}

	return state;
};

const setDevice = (state: IVisibilityState, device?: Models.Device, deviceId?: string): IVisibilityState => {
	if (!device || !deviceId) {
		return state;
	}

	if (state.shipment) {
		device.shipment = state.shipment;
	}

	return {
		...state,
		device: device.clone(),
		currentDeviceId: deviceId,
		history: new Map<string, Types.TProperty[]>(),
		points: Components.MapUtils.buildPoints(new Array<Types.TPin>(), state.history.get(`${device.id}#${device.shipment.id}#location`) as Types.TLocationProperty[]),
		sensors: device.getSelectedSensorsConfig(),
		present: device.clone().present,
	};
};

const updateStateByMqtt = (state: IVisibilityState, payload: TAction): IVisibilityState => {
	logger.debug("updateStateByMqtt", payload);
	const msg = JSON.parse(payload.message as string) as Interfaces.IAnyObject;

	if ((!msg.Device && !msg.segments) || !state.devices.length || !state.shipment) {
		logger.debug("\n\n discard Mqtt message processing");

		return state;
	}

	const newState = {
		...state,
		// devices: _devices,
	};

	if (msg.segments && (msg.segments as Interfaces.IAnyObject[]).length) {
		const { segments } = msg;
		(segments as Interfaces.IAnyObject[]).forEach((segment: any) => {
			const segmentIndex: number | undefined = newState.shipment?.segments.findIndex(s => s?.id === segment.segmentId);
			if (segmentIndex !== undefined && newState.shipment?.segments[segmentIndex]) {
				const _segment = newState.shipment?.segments[segmentIndex];
				_segment.status = segment.status;
				const { milestones } = segment;
				if (milestones && milestones.length) {
					milestones.forEach((milestone: any) => {
						const milestoneIndex: number | undefined = newState.shipment?.segments[segmentIndex].milestones.findIndex(m => m?.id === milestone.milestoneId);
						if (milestoneIndex !== undefined && newState.shipment?.segments[segmentIndex].milestones[milestoneIndex]) {
							const _milestone = newState.shipment?.segments[segmentIndex].milestones[milestoneIndex];
							_milestone.actualEpochTime = milestone.actualEpochTime;
						} else {
							newState.shipment?.segments[segmentIndex].milestones.push(new Models.Milestone({
								ActualEpochTime: milestone.actualEpochTime,
								MilestoneId: milestone.milestoneId,
								Status: "Completed"
							}));
						}
					});
				}
			}
		});
		if (!state.trackingEndTime && state.shipment && state.shipment.leg && state.shipment.leg === "End-to-End") { // for values 0 || undefined
			// check if we should start(for trackingEndTime=0) or stop(for trackingEndTime=undefined) realtime tracking.
			const { startTime, endTime, inProgress } = Utils.getHistoryEndToEndInterval(state.shipment, state.orderId || "", state.legs, state.isLiveLegExtended);
			if (!inProgress) {
				newState.trackingEndTime = endTime || newState.trackingEndTime;
			} else {
				newState.trackingEndTime = undefined;
			}
			if (newState.trackingEndTime !== undefined && !inProgress) {
				logger.debug("\n\n discard Mqtt message processing 3");

				return newState;
			}
		}
	}
	if (newState.trackingEndTime !== undefined) {
		logger.debug("\n\n discard Mqtt message processing 4");

		return newState;
	}
	const deviceIndex: number = state.devices.findIndex(d => d?.id === msg.Device);
	logger.debug("updateStateByMqtt", deviceIndex);
	if (deviceIndex === -1) {
		return newState;
	}

	// const _devices = cloneDeep(state.devices);
	// _devices[deviceIndex].present.update(msg);
	// _devices[deviceIndex].updateAlarms(msg);


	const _device = state.devices[deviceIndex].clone();
	const _present = cloneDeep(state.present);
	if (_device.id === state.device?.id) {
		_present?.update(msg);
		logger.debug("updateStateByMqtt", _present ? _present["tempProbe"] : "rrr");
		newState.present = _present;
	}

	return addStateToHistory(newState, _device.id, state.shipment.id, Models.parseState(msg, _device.type));
};

const updateDevicePresent = (state: IVisibilityState, payload: TAction): IVisibilityState => {
	const { present } = payload;
	const newState = {
		...state,
	};
	if (present) {
		Array.from(present.keys() ?? []).forEach((sensor: string) => {
			if (newState.present) {
				newState.present[sensor] = present.get(sensor);
			}
		});
	}

	return newState;
};

export const VisibilityReducer: Reducer<IVisibilityState, DispatchAction<TAction>> = (state = initialState, action) => {
	switch (action.type) {
		case VisibilityActionTypes.RESET_STATE:
			return {
				...initialState,
			};
		case VisibilityActionTypes.SET_LOADING_SHIPMENT:
			return {
				...state,
				isLoadingShipment: action.payload.isLoadingShipment,
				error: undefined,
			};
		case VisibilityActionTypes.SET_LOADING_SHIPMENTS:
			return {
				...state,
				isLoadingShipments: action.payload.isLoadingShipments,
				error: undefined,
			};
		case VisibilityActionTypes.SET_LOADING_HISTORY: {
			return setLoadingHistory(state, action.payload);
		}
		case VisibilityActionTypes.SET_SHIPMENT: {
			return setShipment(state, action.payload);
		}
		case VisibilityActionTypes.SET_SHIPMENTS:
			return {
				...state,
				shipments: (action.payload as { shipments: Models.Shipment[] }).shipments,
			};
		case VisibilityActionTypes.SET_DEVICE: {
			return setDevice(state, action.payload.device, action.payload.currentDeviceId);
		}
		case VisibilityActionTypes.SET_CURRENT_DEVICE_ID: {
			return {
				...state,
				currentDeviceId: action.payload.currentDeviceId,
			};
		}
		case VisibilityActionTypes.UPDATE_ALARM: {
			return updateAlarm(state, action.payload);
		}
		case VisibilityActionTypes.RESET_IS_CLEAR_ALL_ALARMS: {
			return {
				...state,
				isClearAllAlarms: false,
				clearAllAlarmsReason: "",
			};
		}
		case VisibilityActionTypes.SET_IS_CLEAR_ALL_ALARMS: {
			return {
				...state,
				isClearAllAlarms: true,
			};
		}
		case VisibilityActionTypes.ADD_HISTORY: {
			return addHistory(state, action.payload);
		}
		case VisibilityActionTypes.CLEAR_HISTORY: {
			return {
				...state,
				history: new Map<string, Types.TProperty[]>(),
			};
		}
		case VisibilityActionTypes.SET_SENSORS: {
			return {
				...state,
				sensors: action.payload.sensors ?? [],
			};
		}
		case VisibilityActionTypes.SET_SELECTED_SENSOR: {
			return {
				...state,
				selectedSensor: action.payload.sensor,
			};
		}
		case VisibilityActionTypes.RESET_SELECTED_SENSOR: {
			return {
				...state,
				selectedSensor: undefined,
				sensors: action.payload.sensors ?? [],
			};
		}
		case VisibilityActionTypes.SET_MULTIPLE_PLACE_COMPONENTS: {
			return {
				...state,
				multiplePlaceComponent: action.payload.component ?? "graph",
			};
		}
		case VisibilityActionTypes.SET_ERROR: {
			return {
				...state,
				error: action.payload.error,
			};
		}
		case VisibilityActionTypes.SET_RULES: {
			return {
				...state,
				rules: action.payload.rules,
			};
		}
		case VisibilityActionTypes.SET_LOADING_RULES: {
			const rulesLoading: Map<string, boolean> = new Map(state.rulesLoading);
			rulesLoading.set(`${action.payload.deviceId ?? ""}#${action.payload.shipmentId ?? ""}`, action.payload.isLoadingRules as boolean);

			return {
				...state,
				rulesLoading,
			};
		}
		case VisibilityActionTypes.UPDATE_STATE_BY_MQTT: {
			return updateStateByMqtt(state, action.payload);
		}
		case VisibilityActionTypes.SET_IS_VIRTUAL_SHIPMENT: {
			return {
				...state,
				isVirtualShipment: action.payload.isVirtual,
			};
		}
		case VisibilityActionTypes.SET_ORDER_ID: {
			return {
				...state,
				orderId: action.payload.orderId,
			};
		}
		case VisibilityActionTypes.SET_TRACKING_TIMES: {
			return {
				...state,
				trackingStartTime: action.payload.start ?? state.trackingStartTime,
				trackingEndTime: action.payload.end ?? state.trackingEndTime,
			};
		}
		case VisibilityActionTypes.UPDATE_DEVICE_PRESENT: {
			return updateDevicePresent(state, action.payload);
		}
		case VisibilityActionTypes.SET_IS_UPDATE_DEVICE_PRESENT: {
			return {
				...state,
				isUpdateDevicePresent: action.payload.isUpdateDevicePresent,
			};
		}
		case VisibilityActionTypes.SET_VIEW: {
			return {
				...state,
				view: action.payload.view,
			};
		}
		case VisibilityActionTypes.SET_LEGS: {
			return {
				...state,
				legs: action.payload.legs ?? [],
			};
		}
		case VisibilityActionTypes.SET_IS_LIVE_LEG_EXTENDED: {
			return {
				...state,
				isLiveLegExtended: action.payload.isLiveLegExtended || false,
			};
		}
		default:
			return state;
	}
};
